import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Mail, Loader2, CheckCircle, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter your email address");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 p-6">
        <Card className="w-full max-w-[400px] bg-slate-900/80 backdrop-blur-xl border-slate-800/60">
          <CardContent className="p-8 text-center">
            <div className="p-3 rounded-full bg-emerald-500/10 border border-emerald-500/20 inline-block mb-4">
              <CheckCircle className="h-8 w-8 text-emerald-400" />
            </div>
            <h2 className="text-lg font-bold text-white mb-2">Check Your Email</h2>
            <p className="text-sm text-slate-400 mb-2">
              If an account exists for <span className="text-white font-medium">{email}</span>, we've sent a password reset link.
            </p>
            <p className="text-xs text-slate-500 mb-6">The link expires in 1 hour.</p>
            <div className="space-y-3">
              <Button
                variant="outline"
                className="w-full border-slate-700 text-slate-300 hover:bg-slate-800"
                onClick={() => { setSent(false); setEmail(""); }}
                data-testid="try-another-email-btn"
              >
                Try another email
              </Button>
              <Link to="/login" className="block">
                <Button className="w-full bg-blue-600 hover:bg-blue-500" data-testid="back-to-login-btn">
                  Back to Login
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 p-6">
      <Card className="w-full max-w-[400px] bg-slate-900/80 backdrop-blur-xl border-slate-800/60 shadow-2xl shadow-black/20">
        <CardContent className="p-8">
          <div className="text-center mb-8">
            <div className="p-3 rounded-full bg-blue-500/10 border border-blue-500/20 inline-block mb-4">
              <Mail className="h-8 w-8 text-blue-400" />
            </div>
            <h1 className="text-xl font-bold text-white tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Forgot Password?
            </h1>
            <p className="text-xs text-slate-500 mt-2">
              Enter your email and we'll send you a reset link
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label className="text-xs text-slate-400 font-medium uppercase tracking-wider">Email Address</Label>
              <Input
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="forgot-email"
                className="h-11 bg-slate-950/50 border-slate-800 focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/20 text-slate-200 placeholder:text-slate-600"
              />
            </div>

            <Button
              type="submit"
              className="w-full h-11 bg-blue-600 hover:bg-blue-500 text-white font-semibold shadow-lg shadow-blue-600/20 transition-all active:scale-[0.98]"
              disabled={loading || !email}
              data-testid="forgot-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                "Send Reset Link"
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <Link to="/login" className="text-sm text-slate-500 hover:text-slate-300 inline-flex items-center gap-1" data-testid="back-to-login-link">
              <ArrowLeft className="h-3 w-3" /> Back to login
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
